<template>
  <el-drawer
    :model-value="modelValue"
    :title="$t('界面设置')"
    direction="rtl"
    size="420px"
    append-to-body
    destroy-on-close
    @open="loadAppearance"
    @close="closeDialog"
  >
    <div class="appearance-settings">
      <section class="setting-section">
        <div class="setting-title">
          <span>{{ $t("字体大小") }}</span>
          <span class="setting-value">{{ form.fontSize }}px</span>
        </div>
        <el-slider
          v-model="form.fontSize"
          :min="FONT_SIZE_MIN"
          :max="FONT_SIZE_MAX"
          :step="1"
          show-stops
        />
        <div class="font-size-labels">
          <span>{{ $t("较小") }}</span>
          <span>{{ $t("标准") }}</span>
          <span>{{ $t("较大") }}</span>
        </div>
        <div class="font-preview" :style="{ fontSize: `${form.fontSize}px` }">
          {{ $t("字体预览：瑞秋国际物流") }}
        </div>
      </section>

      <section class="setting-section">
        <div class="setting-title">{{ $t("背景模式") }}</div>
        <div class="theme-options">
          <button
            type="button"
            class="theme-option light-option"
            :class="{ active: form.theme === 'light' }"
            @click="form.theme = 'light'"
          >
            <span class="theme-preview">
              <span class="preview-sidebar"></span>
              <span class="preview-content"></span>
            </span>
            <span class="theme-name">
              <el-icon><Sunny /></el-icon>
              {{ $t("白天") }}
            </span>
          </button>

          <button
            type="button"
            class="theme-option dark-option"
            :class="{ active: form.theme === 'dark' }"
            @click="form.theme = 'dark'"
          >
            <span class="theme-preview">
              <span class="preview-sidebar"></span>
              <span class="preview-content"></span>
            </span>
            <span class="theme-name">
              <el-icon><Moon /></el-icon>
              {{ $t("夜晚") }}
            </span>
          </button>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="drawer-footer">
        <el-button @click="resetAppearance">{{ $t("恢复默认") }}</el-button>
        <div>
          <el-button @click="closeDialog">{{ $t("取消") }}</el-button>
          <el-button type="primary" @click="saveSettings">
            {{ $t("保存") }}
          </el-button>
        </div>
      </div>
    </template>
  </el-drawer>
</template>

<script setup>
import { reactive } from "vue";
import { ElMessage } from "element-plus";
import { useI18n } from "vue-i18n";
import {
  DEFAULT_APPEARANCE,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  getAppearance,
  saveAppearance,
} from "@/utils/appearance";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);
const { t } = useI18n();
const form = reactive({ ...DEFAULT_APPEARANCE });

const loadAppearance = () => {
  Object.assign(form, getAppearance());
};

const resetAppearance = () => {
  Object.assign(form, DEFAULT_APPEARANCE);
};

const closeDialog = () => {
  emit("update:modelValue", false);
};

const saveSettings = () => {
  saveAppearance(form);
  ElMessage.success(t("界面设置已保存"));
  closeDialog();
};
</script>

<style scoped lang="scss">
.appearance-settings {
  padding: 0 4px;
}

.setting-section + .setting-section {
  margin-top: 26px;
}

.setting-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  color: var(--app-text-color);
  font-weight: 600;
}

.setting-value {
  color: #f26522;
  font-weight: 500;
}

.font-size-labels {
  display: flex;
  justify-content: space-between;
  margin-top: -8px;
  color: var(--app-text-secondary-color);
  font-size: var(--el-font-size-extra-small);
}

.font-preview {
  margin-top: 16px;
  padding: 14px;
  color: var(--app-text-color);
  text-align: center;
  background: var(--app-bg-color);
  border: 1px solid var(--app-border-color);
  border-radius: 8px;
}

.theme-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}

.theme-option {
  padding: 8px;
  color: var(--app-text-color);
  background: transparent;
  border: 2px solid var(--app-border-color);
  border-radius: 10px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover,
  &.active {
    border-color: #f26522;
  }

  &.active {
    box-shadow: 0 0 0 2px rgb(242 101 34 / 12%);
  }
}

.theme-preview {
  display: flex;
  height: 72px;
  overflow: hidden;
  border-radius: 6px;
}

.preview-sidebar {
  width: 32%;
}

.preview-content {
  flex: 1;
  margin: 10px;
  border-radius: 4px;
}

.light-option {
  .theme-preview {
    background: #f3f5f8;
  }

  .preview-sidebar {
    background: #fff;
  }

  .preview-content {
    background: #fff;
  }
}

.dark-option {
  .theme-preview {
    background: #0f1115;
  }

  .preview-sidebar {
    background: #1b1d22;
  }

  .preview-content {
    background: #292c33;
  }
}

.theme-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 9px;
}

.drawer-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
}
</style>
